import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo } from "react";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { RecordManagement } from "app/class/record-management";
import { useAuth } from "./AuthProvider";
import {
    createMuiThemeFromPlatformTheme,
    FALLBACK_PLATFORM_THEME,
    normalizePlatformTheme,
    platformThemeCssVariables,
    PlatformThemeRecord,
} from "app/theme/platformTheme";
import queryKeys from "shared/services/queryKeys";
import attachmentApi from "app/services/http-services/attachment/attachment-api";
import { DEFAULT_LOGO_MARK_URL, defaultWordmarkUrl, resolveThemeLogos, themeQueryKey } from "./themeLogos";

interface PlatformThemeContextValue {
    activeTheme: PlatformThemeRecord;
    themes: PlatformThemeRecord[];
    logoMarkUrl: string;
    logoWordmarkUrl: string | null;
    isLoading: boolean;
    hasCachedTheme: boolean;
    setUserTheme: (themeId: string | null) => Promise<void>;
}

const PlatformThemeContext = createContext<PlatformThemeContextValue | null>(null);

const isEnabled = (value: unknown, fallback = true) => {
    if (value === true || value === "true" || value === 1 || value === "1") return true;
    if (value === false || value === "false" || value === 0 || value === "0") return false;
    return fallback;
};

const findActiveTheme = (themes: PlatformThemeRecord[], userThemeId?: string | null) => {
    const activeThemes = themes.filter((theme) => theme.active !== false);
    return normalizePlatformTheme(
        activeThemes.find((theme) => theme.id === userThemeId) ||
        activeThemes.find((theme) => theme.is_default) ||
        activeThemes[0] ||
        FALLBACK_PLATFORM_THEME
    );
};

export const PlatformThemeProvider = ({ children }: { children: ReactNode }) => {
    const { user } = useAuth();
    const queryClient = useQueryClient();
    const cachedTheme = useMemo(() => {
        if (!user?.id) return null;
        try {
            const stored = localStorage.getItem(`prm.theme.last.${user.id}`);
            if (!stored) return null;
            const parsed: unknown = JSON.parse(stored);
            if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
            const theme = parsed as PlatformThemeRecord;
            return typeof theme.id === "string" && typeof theme.name === "string"
                ? normalizePlatformTheme(theme)
                : null;
        } catch (_error) {
            return null;
        }
    }, [user?.id]);

    const { data: themes = [], isLoading: areThemesLoading } = useQuery({
        queryKey: themeQueryKey,
        queryFn: async () => {
            const response = await new RecordManagement<PlatformThemeRecord>("sys_themes")
                .where("active", true)
                .run();

            return response.data.map((theme) => normalizePlatformTheme(theme));
        },
        enabled: Boolean(user?.id),
        staleTime: 1000 * 60 * 5,
    });

    const selectedTheme = useMemo(
        () => areThemesLoading && cachedTheme
            ? cachedTheme
            : findActiveTheme(themes, (user as any)?.theme_id || (user as any)?.themeId),
        [areThemesLoading, cachedTheme, themes, user]
    );
    useEffect(() => {
        if (!user?.id || areThemesLoading || !themes.length) return;
        try {
            localStorage.setItem(`prm.theme.last.${user.id}`, JSON.stringify(selectedTheme));
        } catch (_error) {
            // Storage is optional; the resolved theme remains available in memory.
        }
    }, [areThemesLoading, selectedTheme, themes.length, user?.id]);
    const showLogoWordmark = isEnabled(selectedTheme.show_logo_wordmark, true);

    const { data: themeLogos, isLoading: areThemeLogosLoading } = useQuery({
        queryKey: ["platform-theme-logos", selectedTheme.id, selectedTheme.mode, showLogoWordmark],
        queryFn: () => resolveThemeLogos(selectedTheme.id!, showLogoWordmark, attachmentApi, selectedTheme.mode as "light" | "dark"),
        enabled: Boolean(user?.id && selectedTheme.id && selectedTheme.id !== FALLBACK_PLATFORM_THEME.id),
        retry: false,
        staleTime: 1000 * 60 * 5,
    });

    const logoMarkUrl = themeLogos?.logoMarkUrl || DEFAULT_LOGO_MARK_URL;
    const logoWordmarkUrl = showLogoWordmark ? (themeLogos?.logoWordmarkUrl || defaultWordmarkUrl(selectedTheme.mode as "light" | "dark")) : null;

    const activeTheme = useMemo(() => ({
        ...selectedTheme,
        logo_mark_url: logoMarkUrl,
        logo_wordmark_url: logoWordmarkUrl,
    }), [logoMarkUrl, logoWordmarkUrl, selectedTheme]);

    const muiTheme = useMemo(() => createMuiThemeFromPlatformTheme(activeTheme), [activeTheme]);

    useEffect(() => {
        const root = document.documentElement;
        const variables = platformThemeCssVariables(activeTheme);

        Object.entries(variables).forEach(([key, value]) => {
            root.style.setProperty(key, value);
        });
        root.dataset.primumTheme = activeTheme.name || "primum_light";
        root.dataset.primumThemeMode = activeTheme.mode || "light";
    }, [activeTheme]);

    const setUserTheme = useCallback(async (themeId: string | null) => {
        if (!user?.id) return;

        await new RecordManagement("sys_users").update(user.id, {
            theme_id: themeId,
        });

        await queryClient.invalidateQueries({ queryKey: queryKeys.session.me() });
        await queryClient.invalidateQueries({ queryKey: queryKeys.records.detail("sys_users", user.id) });
    }, [queryClient, user?.id]);

    const value = useMemo<PlatformThemeContextValue>(() => ({
        activeTheme,
        themes,
        logoMarkUrl,
        logoWordmarkUrl,
        isLoading: areThemesLoading || areThemeLogosLoading,
        hasCachedTheme: Boolean(cachedTheme && areThemesLoading),
        setUserTheme,
    }), [activeTheme, areThemeLogosLoading, areThemesLoading, cachedTheme, logoMarkUrl, logoWordmarkUrl, setUserTheme, themes]);

    return (
        <PlatformThemeContext.Provider value={value}>
            <ThemeProvider theme={muiTheme}>
                <CssBaseline />
                {children}
            </ThemeProvider>
        </PlatformThemeContext.Provider>
    );
};

export const usePlatformTheme = () => {
    const context = useContext(PlatformThemeContext);
    if (!context) {
        throw new Error("usePlatformTheme must be used inside PlatformThemeProvider");
    }

    return context;
};
